import { useReducer, useCallback } from 'react';

/**
 * use a record as state that can be added to, removed from, replaced, or reset
 * @param init
 * @returns
 */
export const useRecordState = <T>(init: Record<string, T> = {}) => {
  const [state, dispatch] = useReducer(
    (
      previous: Record<string, T>,
      payload:
        | { action: 'add'; change: Record<string, T> }
        | { action: 'update'; change?: Record<string, T> }
        | {
            action: 'remove';
            change: Record<string, T> | ((it: [string, T]) => boolean);
          },
    ): Record<string, T> => {
      if (payload.action === 'update') {
        return payload.change || init;
      }
      if (payload.action === 'add') {
        return { ...previous, ...payload.change };
      }
      if (payload.action === 'remove') {
        const removed = new Set(Object.keys(payload.change));
        return Object.fromEntries(
          Object.entries(previous).filter(([it]) => removed.has(it)),
        );
      }
      return previous;
    },
    init,
  );
  const add = useCallback(
    (change: Record<string, T>) => dispatch({ action: 'add', change }),
    [dispatch],
  );
  const remove = useCallback(
    (change: Record<string, T>) => dispatch({ action: 'remove', change }),
    [dispatch],
  );
  const update = useCallback(
    (change?: Record<string, T>) => dispatch({ action: 'update', change }),
    [dispatch],
  );
  return {
    state,
    add,
    remove,
    update,
  };
};
/**
 * use an array as state that can be added to, removed from, replaced, or reset
 * @param init
 * @returns
 */
export const useCollectionState = <T>(init: T[] = []) => {
  const [state, dispatch] = useReducer(
    (
      previous: T[],
      payload:
        | { action: 'add'; change: T[] }
        | { action: 'update'; change?: T[] }
        | { action: 'remove'; change: T[] | ((it: T) => boolean) },
    ): T[] => {
      if (payload.action === 'update') {
        return payload.change || init;
      }
      if (payload.action === 'add') {
        return [...previous, ...payload.change];
      }
      if (payload.action === 'remove') {
        const { change } = payload;
        if (typeof change === 'function') {
          const removed = new Set(previous.filter((it) => !change(it)));
          return previous.filter((it) => removed.has(it));
        }
        const removed = new Set(change);
        return previous.filter((it) => removed.has(it));
      }
      return previous;
    },
    init,
  );
  const add = useCallback(
    (change: T[]) => dispatch({ action: 'add', change }),
    [dispatch],
  );
  const remove = useCallback(
    (change: T[] | ((it: T) => boolean)) =>
      dispatch({ action: 'remove', change }),
    [dispatch],
  );
  const update = useCallback(
    (change?: T[]) => dispatch({ action: 'update', change }),
    [dispatch],
  );
  return {
    state,
    add,
    remove,
    update,
  };
};

/**
 * use a boolean as state that can be toggled between true/false
 * @example
 * const [openState, toggleFunction] = useToggleState();
 */
export const useToggleState = (init = false) =>
  useReducer((previous: boolean, _: unknown): boolean => !previous, init) as [
    boolean,
    () => void,
  ];
